import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import type { Effort, SkillId } from "../lib/types";

export function Chip({
  tone = "mute",
  children,
}: {
  tone?: "accent" | "recovery" | "warn" | "danger" | "mute";
  children: ReactNode;
}) {
  const cls = {
    accent: "bg-accent/15 text-accent",
    recovery: "bg-recovery/15 text-recovery",
    warn: "bg-warn/15 text-warn",
    danger: "bg-danger/15 text-danger",
    mute: "bg-surface-2 text-ink-mute",
  }[tone];
  return <span className={`rounded-chip px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

export function effortTone(effort: Effort): "accent" | "recovery" | "warn" | "danger" | "mute" {
  return ({ rest: "recovery", light: "recovery", moderate: "accent", hard: "warn" } as const)[
    effort
  ];
}

export function skillTone(skill: SkillId | string): "accent" | "recovery" {
  return skill === "recovery" || skill === "mobility" ? "recovery" : "accent";
}

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  type = "button",
  className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "ghost" | "danger";
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) {
  const cls = {
    primary: "bg-accent text-page hover:bg-accent-soft",
    ghost: "bg-surface-2 text-ink hover:bg-border",
    danger: "bg-danger/15 text-danger hover:bg-danger/25",
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`tap rounded-chip px-3.5 py-2 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-40 ${cls} ${className}`}
    >
      {children}
    </button>
  );
}

/** Bottom sheet. Thumb-reachable, dismisses on backdrop tap or Escape. */
export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/55" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className="card animate-fade-up max-h-[88dvh] w-full max-w-md overflow-y-auto rounded-b-none p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-lift"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          {title ? <h2 className="text-xl font-semibold">{title}</h2> : <span />}
          <button
            aria-label="Close"
            className="tap rounded-chip p-1.5 text-ink-mute hover:bg-surface-2"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-ink-soft">{label}</span>
      {children}
    </label>
  );
}

export function NumberInput({
  value,
  onChange,
  step = 1,
  min = 0,
  placeholder,
  inputMode = "decimal",
}: {
  value: number | "";
  onChange: (v: number | "") => void;
  step?: number;
  min?: number;
  placeholder?: string;
  inputMode?: "decimal" | "numeric";
}) {
  return (
    <input
      type="number"
      inputMode={inputMode}
      step={step}
      min={min}
      placeholder={placeholder}
      className="field numeral focus:field-focus"
      value={value}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    />
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-ink-mute">{children}</p>;
}

export function fmtDate(iso: string) {
  const d = new Date(iso.length === 10 ? iso + "T12:00:00" : iso);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
